import { SEARCH_RESULTS_PANE } from '@disco/constants/PaneTypes'

import type { ExplorerPane } from 'types/explorer'

// OpenSearch stops counting matches at 10,000
const SEARCH_RESULTS_TOTAL_CAP = 10000

export type SearchResultsCount = {
  count: number
  isLowerBound: boolean
}

/**
 * Result count for a global search-results pane, or `undefined` for anything
 * but a settled keyword/filter search. Holds no display strings.
 */
export function getSearchResultsCount(
  pane: ExplorerPane,
): SearchResultsCount | undefined {
  const { results, resultsTotal } = pane

  if (pane.type !== SEARCH_RESULTS_PANE || pane.groupId) return undefined
  if (pane.searching || pane.searchError || pane.simSearch) return undefined
  if (!Array.isArray(results)) return undefined

  const hasTotal = typeof resultsTotal === 'number'
  const count = hasTotal ? resultsTotal : results.length
  const isLowerBound = hasTotal
    ? count >= SEARCH_RESULTS_TOTAL_CAP
    : Boolean(pane.next)

  return { count, isLowerBound }
}

/**
 * The only untranslated copy in this module: swap this body for a `t()` call
 * with `_one`/`_other` plural keys once the explorer gains an i18next namespace.
 */
export function formatSearchResultsTitle({
  count,
  isLowerBound,
}: SearchResultsCount): string {
  const formattedCount = count.toLocaleString('en-US')
  const noun = count === 1 && !isLowerBound ? 'result' : 'results'

  return `${formattedCount}${isLowerBound ? '+' : ''} ${noun}`
}

/** Pane-header title; `undefined` tells the caller to fall back to `pane.name`. */
export function getSearchResultsTitle(pane: ExplorerPane): string | undefined {
  const resultsCount = getSearchResultsCount(pane)

  return resultsCount && formatSearchResultsTitle(resultsCount)
}
